import React, { useState } from "react";
import Image from "next/image";
import moment from "moment";
import "moment/locale/hy-am";
import useLikes from "hooks/predictions/useLikes";
import useAuth from "hooks/auth/useAuth";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import ReplyIcon from "@mui/icons-material/Reply";
import useDarkMode from "hooks/useDarkMode";

const CommentItem = ({ comment, docPath, onReply, replies = [] }) => {
  const { currentUser } = useAuth();
  const { likeCount, toggleLike, checkIfLiked } = useLikes(
    `${docPath}/comments/${comment.id}`,
  );
  const isLiked = checkIfLiked(currentUser?.uid);
  const [showReplyInput, setShowReplyInput] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [showReplies, setShowReplies] = useState(false);
  const { theme } = useDarkMode();

  const handleReplySubmit = () => {
    if (!replyText.trim()) return;
    const actualParentId = comment.parentId || comment.id;
    const replyToUserName = comment.parentId ? comment.userName : null;
    onReply(replyText, actualParentId, replyToUserName);
    setReplyText("");
    setShowReplyInput(false);
  };

  return (
    <div className="flex flex-col gap-3 group">
      <div className="flex flex-row gap-3">
        <div className="shrink-0">
          {comment.userImage ? (
            <Image
              src={comment.userImage}
              alt={comment.userName}
              width={32}
              height={32}
              className="rounded-full ring-1 ring-cyan-500/30"
            />
          ) : (
            <div className="w-8 h-8 rounded-full bg-cyan-500/20 flex items-center justify-center text-cyan-400 font-bold text-xs">
              {comment.userName.charAt(0).toUpperCase()}
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0 flex flex-col">
          <div className="bg-slate-800/40 border-slate-700/50 light:bg-slate-100/80 light:border-slate-200 rounded-2xl px-3 py-1.5 border break-words overflow-hidden">
            <div className="flex justify-between items-start gap-2 mb-0.5">
              <span className="text-[13px] font-bold text-cyan-50 light:text-slate-900 leading-tight">
                {comment.userName}
                {comment.replyToUserName && (
                  <span className="text-cyan-500/60 light:text-cyan-600 font-normal ml-1">
                    <span className="text-cyan-400 font-bold whitespace-nowrap">
                      @{comment.replyToUserName}
                    </span>
                  </span>
                )}
              </span>
              <span className="text-[9px] text-slate-400 light:text-slate-500  whitespace-nowrap pt-0.5">
                {comment.createdAt
                  ? moment(comment.createdAt.toDate()).locale("hy-am").fromNow()
                  : "քիչ առաջ"}
              </span>
            </div>
            <p className="text-[13px] text-slate-200 light:text-slate-700 leading-snug">
              {comment.text}
            </p>
          </div>

          <div className="flex flex-row gap-4 mt-1 px-2">
            <button
              onClick={() => toggleLike(currentUser?.uid)}
              className={`flex items-center gap-1 text-[10px] font-bold transition-colors ${isLiked ? "text-rose-500" : "text-slate-400 hover:text-cyan-400"}`}
            >
              {isLiked ? (
                <FavoriteIcon sx={{ fontSize: 13 }} />
              ) : (
                <FavoriteBorderIcon sx={{ fontSize: 13 }} />
              )}
              {likeCount > 0 ? likeCount : "Հավանել"}
            </button>
            <button
              onClick={() => setShowReplyInput(!showReplyInput)}
              className="flex items-center gap-1 text-[10px] font-bold text-slate-400 hover:text-cyan-400 transition-colors"
            >
              <ReplyIcon sx={{ fontSize: 13 }} />
              Պատասխանել
            </button>
            {replies.length > 0 && (
              <button
                onClick={() => setShowReplies(!showReplies)}
                className="text-[10px] font-bold text-cyan-500/80 hover:text-cyan-400 transition-colors"
              >
                {showReplies
                  ? "Թաքցնել պատասխանները"
                  : `Դիտել պատասխանները (${replies.length})`}
              </button>
            )}
          </div>

          {showReplyInput && (
            <div className="flex flex-row gap-2 mt-3 pl-2">
              <input
                type="text"
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Գրեք պատասխանը..."
                className="flex-1 bg-slate-900/50 border-slate-700 text-cyan-50 light:bg-slate-100 light:border-slate-200 light:text-slate-900 border rounded-full px-4 py-1.5 text-xs focus:outline-none focus:border-cyan-500/50 transition-colors"
                onKeyDown={(e) => e.key === "Enter" && handleReplySubmit()}
              />
              <button
                onClick={handleReplySubmit}
                className="bg-cyan-600 hover:bg-cyan-500 text-white px-3 py-1 rounded-full text-[10px] font-bold transition-colors"
              >
                Ուղարկել
              </button>
            </div>
          )}

          {/* Replies */}
          {showReplies && replies.length > 0 && (
            <div className="flex flex-col gap-3 mt-3 pl-2.5 md:pl-4 border-l border-slate-700/50">
              {replies.map((reply) => (
                <CommentItem
                  key={reply.id}
                  comment={reply}
                  docPath={docPath}
                  onReply={onReply}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CommentItem;
